import { useEffect, useRef, useState } from "react";
import { themeVar as colors } from "../design-tokens";
import {
  REPORT_TIERS, REPORT_TIER_LABELS, REPORT_TIER_HINTS, type ReportTier,
  classifyUpload, SUPPORTED_IMAGE_EXTENSIONS, UNSUPPORTED_IMAGE_EXTENSIONS,
} from "./constants";
import { prepareSubjectPhoto } from "./preparePhoto";
import { SubjectAddressStep } from "./SubjectAddressStep";
import type { CadPick, SubjectChangeEntry } from "./cadPick";
import { CascadeNotices } from "./CascadePanels";
import type { CascadeNotice } from "./cascade";
import type { SubjectGrid } from "./types";
import { usePasteFiles } from "./usePasteFiles";
import { SourcePrecedencePreview, precedencePreviewEnabled } from "./SourcePrecedence";
import { useUsage } from "../lib/useUsage";
import { atUsageLimit } from "../lib/usageApi";
import { LimitNoticeView, ServerLimitNotice, UsageLineView } from "../ui/UsageWidgets";

/**
 * Guided step 1 (VI-1): "The agent may drop file(s): the comps export and/or a subject
 * full-report/CAD sheet." Files are A source, not the final word — they feed the confirm grid in
 * step 2. Guided-first is the ONLY path in this build: the automated (no-input) flow that
 * fabricated a subject from comp #1 is a removed/hidden path per the locked ADR (VI-0.1), not a
 * toggle this screen offers.
 *
 * Wave 2 changes:
 * - UI-001 — the `<main>` + inline `<img>` chrome moved out to `AppShell`; this renders body only.
 * - UI-051 — the dashed box was the universal drag-and-drop signifier with no drop handler, so a
 *   dragged MLS PDF navigated the browser away and took the app with it. Real handlers now, plus
 *   per-file chips with a remove control (re-picking used to replace the whole array).
 * - UI-052 — the CMA/AMB choice moved here from the readiness gate. It was being *consumed* by the
 *   extract at this step while its only control lived two screens later, inside a branch that
 *   didn't render when fields were missing. Hints are visible text, not a `title` tooltip.
 * - UI-053 — the address label no longer names an internal extract stage or leads with
 *   "(optional —"; NAVIGATION rule 7 bans that framing outright. Help text is its own element,
 *   referenced with `aria-describedby` so a screen reader announces the field name as the name.
 * - UI-054 — errors are `role="alert"`; a permanently-mounted `role="status"` region exists for
 *   the wait (which now genuinely spans the extract, since UI-050 part 1).
 */
/**
 * EVERYTHING THIS SCREEN HOLDS ABOUT THE UPLOAD ITSELF (2026-08-05).
 *
 * It was five pieces of `useState` inside this component, which is fine for a screen you only ever
 * move forward from — and this one has a Back button pointing at it. Pressing "Back to your file"
 * on step 2 unmounts this component, and unmounting destroyed the file list, the photo the agent
 * had picked, and the disclosures attached to it. The address box came back EMPTY on a flow whose
 * next state already held a fully populated subject. `CmaAmbFlow` owns this now; see the state
 * declaration there.
 *
 * `preparing` and `dragging` are deliberately NOT here: both describe an interaction that is
 * happening right now, and neither survives leaving the screen in any meaningful sense.
 */
export interface IntakeState {
  /** The documents: the comps export, the CAD sheet. */
  files: File[];
  /** Images are held apart from documents from the moment they land. One dropzone, two destinations. */
  photos: File[];
  subjectIndex: number;
  /** What we changed on the way through — a conversion or a resize is disclosed, never silent, and
   * the disclosure has to outlive a trip to the next screen and back. */
  photoNotes: string[];
  photoProblems: string[];
}

export const EMPTY_INTAKE: IntakeState = {
  files: [], photos: [], subjectIndex: 0, photoNotes: [], photoProblems: [],
};

export function IntakeScreen({
  onStart,
  busy,
  error,
  tier,
  onTierChange,
  condition,
  onConditionChange,
  addressLine,
  onAddressChange,
  subjectGrid,
  onSubjectGridChange,
  onSubjectClipResolved,
  publicRecordsDormant,
  cadPick,
  onCadPickChange,
  onSubjectChangeLogged,
  intake,
  onIntakeChange,
  notices,
  resolving,
  limitRefusalMessage,
}: {
  onStart: (args: { files: File[]; addressLine: string; subjectPhoto: File | null;
                    localReadOnly: boolean }) => void;
  busy: boolean;
  error?: string | null;
  tier: ReportTier;
  onTierChange: (t: ReportTier) => void;
  /** Threaded through so the condition control inside the address step edits the SAME value step 2
   * shows. Two independent condition states across two screens is how an agent sets it here and
   * finds it back at Average there. */
  condition: string;
  onConditionChange: (v: string) => void;
  /** THE SUBJECT ADDRESS AND THE SUBJECT GRID LIVE IN THE FLOW (2026-08-05). They used to be local
   * state here and separate local state there, copied across once on submit — so steps 1 and 2 were
   * two forms over two pieces of state that happened to start out equal, and going back to step 1
   * landed on an empty box. One form, one piece of state, both directions. */
  addressLine: string;
  onAddressChange: (v: string) => void;
  /** What the address lookup found, already in confirm-grid shape, or null before any lookup has
   * returned anything. */
  subjectGrid: SubjectGrid | null;
  onSubjectGridChange: (g: SubjectGrid) => void;
  /** Pass-through for `SubjectAddressStep`'s resolved Cotality record (see its `onClipResolved`).
   * This screen has no use for it; the flow does, and a clip that stops here is a subject the
   * provider resolves twice. */
  onSubjectClipResolved?: (resolved: { clip: string; address: string } | null) => void;
  /** Pass-through: the public-records connector is dormant, so the address step skips it. */
  publicRecordsDormant?: boolean;
  /** Pass-through for the county CAD record the subject is locked to, and the ledger of switches
   * (`SubjectAddressStep`'s `cadPick` / `onSubjectChangeLogged`). Held by the flow, which saves
   * both with the review and sends the ledger with Generate. */
  cadPick?: CadPick | null;
  onCadPickChange?: (pick: CadPick | null) => void;
  onSubjectChangeLogged?: (entry: SubjectChangeEntry) => void;
  intake: IntakeState;
  onIntakeChange: (next: IntakeState | ((prev: IntakeState) => IntakeState)) => void;
  /** WHAT THIS DEPLOYMENT HAS TO SAY BEFORE THE AGENT TYPES ANYTHING (2026-08-11).
   *
   * Built by `CmaAmbFlow` from `GET /api/next/cascade/status`, which reads the gates and makes NO
   * provider call and spends none of the daily allowance — which is the whole reason it can be
   * shown here rather than after the first lookup. `env` is `sandbox` in production today, so the
   * "not real property data" notice fires on every real run, and it belongs on the first screen: a
   * sandbox record is complete and plausible and may be a different house, and finding that out
   * after reading a grid of figures is finding it out too late. Empty on a deployment with nothing
   * to disclose, and the screen renders exactly as it did before. */
  notices?: CascadeNotice[];
  /** The address-only path is running: no file to read, so no extract progress panel — just the
   * lookup. Kept separate from `busy` (which also covers an upload) so the wait can say which of
   * the two is actually happening. */
  resolving?: boolean;
  /** §6, gap closed 2026-10-02: the SERVER's own `limit_reached` sentence, when the round trip
   * this screen's primary action just made came back refused — the race case (two tabs at 9 of
   * 10) that proactive disabling alone cannot cover. `CmaAmbFlow` clears this on every step
   * change, so it never survives into a screen it was not about. */
  limitRefusalMessage?: string | null;
}) {
  const { files, photos, subjectIndex, photoNotes, photoProblems } = intake;
  const setFiles = (fn: (prev: File[]) => File[]) =>
    onIntakeChange((prev) => ({ ...prev, files: fn(prev.files) }));
  const [preparing, setPreparing] = useState<string[]>([]);
  /** THE FAST READ IS THE DEFAULT NOW; THIS IS THE OPT-OUT (2026-08-07, owner request: "we also
   * need to make the mathpix the fast PDF rendering the first path or main path instead of forcing
   * the user to select it. if they arent in a hurry, it saves tokens to go with the slower option.")
   *
   * What used to sit here was `forceFastPdf`, off by default, labelled "Force fast PDF rendering" —
   * which read as "tick this to go fast" and was not true. The hosted converter was ALREADY tried
   * first for every page our own readers cannot handle; that box only skipped the "does this file
   * need image reading at all" gate, which is a timing-A/B aid and nothing an agent wants. The
   * label cost the owner a multi-minute read on their own 20-page Springtree export while believing
   * the slow path was the only one on offer (measured: ~258s local vs ~15s hosted).
   *
   * So the box now buys the thing an agent might actually want and previously could not ask for:
   * keeping the file on our own servers. Still false by default, still deliberately not remembered
   * between uploads — it is a decision about ONE file (this one is confidential, or I am not in a
   * hurry and would rather not spend the per-page cost), not a setting to leave on and forget. */
  const [localReadOnly, setLocalReadOnly] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Object URLs are a browser resource, not a string — revoked when the set changes or the screen
  // unmounts, or a long intake session leaks one per photo the agent tried.
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = photos.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [photos]);

  const subjectPhoto = photos[subjectIndex] ?? photos[0] ?? null;

  /** Whether the primary action can run: we know which home this is, either because the agent typed
   * it or because they attached a sheet that names it. See the note on the button itself for why it
   * is an OR and not just the address. */
  const armed = (!!addressLine.trim() || files.length > 0) && !busy && !resolving;

  // §6: "Next — check what we found" is this screen's real equivalent of the owner's "county/CAD
  // subject import" + "MLS comp and competition pulls" refusal items — it is the single action
  // that looks the address up against public records AND reads any uploaded comps/CAD/MLS sheet.
  const { usage } = useUsage();
  const limited = atUsageLimit(usage);

  const dedupe = (prev: File[], next: File[]) => {
    const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
    return [...prev, ...next.filter((f) => !seen.has(`${f.name}:${f.size}`))];
  };

  /**
   * @param extraNotes Disclosures about the files themselves, from before they got here — currently
   *   only the paste path, which renames a clipboard image because it arrives without a name (see
   *   `usePasteFiles`). They are carried through rather than set separately because
   *   `prepareSubjectPhoto` REPLACES `photoNotes` when it finishes, so a note written beside this
   *   call would be silently wiped a tick later.
   */
  function addFiles(incoming: File[], extraNotes: string[] = []) {
    if (!incoming.length) return;
    const docs = incoming.filter((f) => classifyUpload(f) === "document");
    const maybePhotos = incoming.filter((f) => classifyUpload(f) !== "document");
    // Documents land and any previous photo problem is cleared in ONE update: two separate calls
    // against a single lifted object would have the second overwrite the first's `files`.
    // Replaced, not appended: a problem the agent already fixed should not stay on screen.
    onIntakeChange((prev) => ({
      ...prev,
      files: docs.length ? dedupe(prev.files, docs) : prev.files,
      photoNotes: extraNotes.length ? extraNotes : prev.photoNotes,
      photoProblems: [],
    }));
    if (!maybePhotos.length) return;

    // A HEIC needs a 2.7 MB WASM decoder and an oversized photo needs a canvas pass, so this is
    // async. Named so the agent knows why the thumbnail has not appeared yet.
    setPreparing(maybePhotos.filter((f) => classifyUpload(f) !== "photo").map((f) => f.name));
    void Promise.all(maybePhotos.map((f) => prepareSubjectPhoto(f))).then((results) => {
      const ready: File[] = [];
      const notes: string[] = [];
      const problems: string[] = [];
      for (const r of results) {
        if (r.file) ready.push(r.file);
        if (r.note) notes.push(r.note);
        if (r.error) problems.push(r.error);
      }
      onIntakeChange((prev) => ({
        ...prev,
        photos: ready.length ? dedupe(prev.photos, ready) : prev.photos,
        // The paste disclosure leads: "we renamed your file and it has no source" outranks
        // "we shrank it", and losing it here is how a silent transform happens.
        photoNotes: [...extraNotes, ...notes],
        photoProblems: problems,
      }));
      setPreparing([]);
    });
  }

  /**
   * ⌘V, the third door into `addFiles` (2026-08-29, owner: "can we allow paste of pictures into the
   * attachment area? or paste of files?").
   *
   * A screenshot of the MLS sheet or the county record is the commonest thing an agent has on the
   * clipboard at a desk, and pasting one did nothing at all before this — no file, no error, which
   * reads as the app being broken rather than as a missing feature.
   *
   * Nothing here bypasses anything: a pasted file meets `classifyUpload`, `prepareSubjectPhoto`,
   * the HEIC decode, the downscale and the dedupe exactly as a dropped one does. The one thing
   * paste needs that drop does not is a NAME — the clipboard supplies none — and because a file
   * with no name also has no source, the rename is disclosed rather than done quietly.
   */
  usePasteFiles({
    onPastedFiles: (pasted, notes) => addFiles(pasted, notes),
    // A pasted block of spreadsheet cells is refused OUT LOUD. Ignoring it silently is the same
    // failure the paste support exists to fix, one level up.
    onPastedTable: (note) => onIntakeChange((prev) => ({ ...prev, photoProblems: [note] })),
    enabled: !busy,
  });

  return (
    /* STEP 1, ON THE APPROVED PROTOTYPE'S OWN LAYOUT (2026-08-30).
     *
     * WHAT CHANGED AND WHY IT TOOK SO LONG. `mock-design.css` — the verbatim port of the approved
     * prototype — has shipped `.thead`, `.panelWrap`, `.panel`, `.lbl` and `.foot` for weeks, live in
     * production and used by nothing. The port covered the app SHELL (the bar, the step rail) and
     * stopped there, so every wizard screen underneath it kept its original single 880px column of
     * inline-styled blocks. The stylesheet said one thing and the screen said another, and the screen
     * is what you look at.
     *
     * This is the prototype's step 1: a full-bleed head, three panels across
     * (`repeat(auto-fit, minmax(320px, 1fr))`, so they stack on a phone without a media query of
     * ours), and a foot that puts the consequence on the left and the action on the right.
     *
     * `measures.ts` carried the note that made this possible: the page caps are load-bearing "until
     * the wizard's content fills — the prototype's arrangement, two columns of real content — and
     * then remove these. That is a layout change per screen, not a constant." This screen is now that
     * layout, so it renders at `content="full"`. Screens still on a single column keep their cap,
     * because uncapping THOSE moves empty margin to one side rather than filling anything.
     *
     * NOT ONE HANDLER, PROP OR PIECE OF STATE MOVED. `onStart`, `addFiles`, `setFiles`, the paste
     * hook, the photo picker, the disclosures, `armed`, `localReadOnly` and every `role="alert"` /
     * `role="status"` region are the same nodes in the same order. This is a change of boxes. */
    <div className="vb-wiz">
      <div className="thead">
        <h2>Start a CMA or Advanced Market Brief</h2>
        {/* ONE REQUIRED THING (2026-08-11, the source cascade). This used to read "Two things: the
            address … and your comps export", and the button below refused to arm without a file — so
            the product's only way in was a document that requires MLS membership to produce, which is
            the exact dependency the cascade exists to remove. The address is the one fact every agent
            has. Everything after it is us. */}
        <span className="c">
          The address is the only thing we need. Nothing generates yet — you review and confirm every
          fact before anything computes.
        </span>
      </div>

      {/* WHAT THIS DEPLOYMENT HAS TO SAY, BEFORE ANYTHING IS FETCHED. See the `notices` prop. */}
      <CascadeNotices notices={notices} />

      {/* AT THE TOP, NOT BURIED BESIDE THE BUTTON. An error about the read is the first thing that
          should be read, and in a three-column layout "just above the action" is a column, not a
          position on the page. */}
      {error && (
        <div className="panelWrap">
          <p role="alert" className="note caut" style={{ gridColumn: "1 / -1", margin: 0 }}>
            {error}
          </p>
        </div>
      )}

      <div className="panelWrap">
        {/* THE SUBJECT PANEL TAKES THE WHOLE ROW ONCE THERE IS A RECORD IN IT (2026-08-30).
         *
         * THE DEFECT THIS CLOSES, AND HOW IT SHIPPED. `SubjectAddressStep` renders a full
         * `ConfirmGrid` as soon as a lookup returns — eighteen editable rows, each a checkbox, a
         * label, a value control and a source tag on one line. That grid needs width. Dropped into
         * one column of a `minmax(320px, 1fr)` three-panel row it collapsed: every label wrapped to
         * one word per line and the value control overlapped the label beside it.
         *
         * It shipped because every render I took of this screen was the EMPTY state. The grid only
         * exists after a successful lookup, and nothing in the sweep ever performed one — so the
         * broken state was never photographed. The fixture harness now drives a lookup on this
         * screen for exactly that reason.
         *
         * Spanning the row rather than shrinking the grid: the other two panels are genuinely
         * narrow things (a choice of three, a dropzone) and read fine below it. The prototype's own
         * third panel is a compact key-value list, not an editable grid, which is why its three
         * columns hold — ours is a heavier component and takes the width it needs. */}
        <div className={subjectGrid ? "panel vb-panel-wide" : "panel"}>
          {/* A HEADING, NOT THE PROTOTYPE'S `<p class="lbl">` (2026-08-30). The mock uses a paragraph
              because a static page has no outline to maintain; this screen has three suites pinning
              one, and they are right to. `.lbl` is a class, so an `<h2>` renders pixel-identically
              and the section is navigable again. */}
          <h2 className="lbl">Which property is this?</h2>
          {/* ADDRESS FIRST (2026-08-04). It used to sit below the dropzone as an optional text box you
              were told you could leave blank, with no lookup attached — so the ordinary path was to
              upload a file, wait out an OCR pass, and only then discover the subject was not in it.
              The address is the one fact the agent always has and the one every provider keys on. */}
          <SubjectAddressStep
            addressLine={addressLine}
            onAddressChange={onAddressChange}
            grid={subjectGrid}
            onGridChange={onSubjectGridChange}
            onClipResolved={onSubjectClipResolved}
            publicRecordsDormant={publicRecordsDormant}
            cadPick={cadPick}
            onCadPickChange={onCadPickChange}
            onSubjectChangeLogged={onSubjectChangeLogged}
            condition={condition}
            onConditionChange={onConditionChange}
            busy={busy}
          />
          <p className="note calm">
            The record you pick is the record every figure in this brief will cite, so it is chosen,
            never guessed. MLS square footage governs once it is checked.
          </p>
        </div>

        {/* THE PAIR ROW (2026-09-01, owner report + `step1-intake-found-dark@1920.png`).
         *
         * THE DEFECT, AND WHY IT SURVIVED A LAYOUT SWEEP. `.panelWrap` is
         * `repeat(auto-fit, minmax(min(100%,320px), 1fr))`, and `auto-fit` earns its keep by
         * COLLAPSING tracks that hold nothing. At 1920 it opens five. Once a lookup returns, the
         * subject panel above takes `grid-column: 1 / -1` — so every track holds something, none
         * collapses, and these two panels sit in tracks one and two at about a third of the width
         * with roughly twelve hundred pixels of empty page beside them.
         *
         * It is invisible in the EMPTY state, where the subject panel is one track of three and
         * the row is full. Every screenshot this repo held of step 1 was the empty state, which is
         * why an owner found it on a real screen first. `shoot.mjs` now has `step1-intake-found`
         * and `fixserver.mjs` answers `/api/cotality/lookup`, so the filled state is photographed
         * from here on.
         *
         * The fix is an explicit two-column row rather than another `auto-fit`: the tier picker is
         * a choice of three and wants a column, the file card wants everything left over, and
         * `align-items: stretch` (the grid default) is what makes the two the same height without
         * either carrying a hard-coded one. */}
        <div className="vb-panel-pair">
        <div className="panel">
          <h2 className="lbl">What kind of report?</h2>
          <TierPicker tier={tier} onChange={onTierChange} />
        </div>

        {/* THE WHOLE CARD IS THE DROP TARGET (2026-09-01, owner directive: "that entire card should
            be allowed to accept drag and drop files instead of a very small section of it").
            The handlers moved from the dashed box up to the panel, so a file let go anywhere on
            this card lands — including on the paragraph, which is where a pointer carrying a file
            naturally ends up. The dashed region stays, because it is what SAYS the card takes
            files; it is now the card's body rather than a small box inside it. */}
        <div
          className="panel vb-dropcard"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(e) => {
            // Only when the pointer leaves the CARD, not on every crossing into a child element —
            // `dragleave` fires for each descendant boundary and would strobe the highlight.
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(Array.from(e.dataTransfer?.files ?? []));
          }}
          style={dragging ? { borderColor: colors.accent, background: colors.accentSoft } : undefined}
        >
          <h2 className="lbl">Your comparable sales — optional</h2>
          {/* SAID IN THOSE WORDS (2026-08-11). Not "you may leave this blank", not a parenthetical:
              the label and the first two words both say optional, because an agent without MLS
              membership cannot produce this file at all and must not read the dropzone as the way in.
              What happens if they skip it is stated here rather than discovered on the next screen. */}
          <p style={{ color: colors.textSecondary, margin: 0, fontSize: 11.5, lineHeight: 1.6 }}>
            {/* IT STARTS WITH THOSE THREE WORDS, AND A TEST PINS THAT (2026-08-30). Trimming the
                paragraph for the panel moved "This is optional." out of the first position, and
                `SourceCascade.test.tsx` matches `/^This is optional\./` on purpose: an agent
                without MLS membership cannot produce this file at all, and must learn that from the
                first three words rather than from a clause in the middle of a paragraph. */}
            This is optional. Drop a comps export, CAD sheet or MLS export anywhere on this card —
            Excel, CSV or PDF. We read it first, and it never overwrites a field you already ticked.
            Leave it empty and we&apos;ll find recent nearby sales from public records for you to
            check.
          </p>

          {/* NO DRAG HANDLERS HERE ANY MORE — they belong to the card (see above), and a second
              set on a child would fight the card's own `dragleave` bookkeeping. This is now the
              card's BODY: `vb-dropcard-zone` grows to fill whatever height the tier panel beside
              it sets, so the target is as tall as the card rather than a 90px box in the middle of
              it. */}
          <div
            className="vb-dropcard-zone"
            style={{
              border: `1px dashed ${dragging ? colors.accent : colors.border}`,
              borderRadius: 8,
              padding: 16,
              textAlign: "center",
              background: dragging ? colors.accentSoft : colors.bgSurface,
            }}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={".csv,.tsv,.txt,.xlsx,.xls,.pdf,.docx,.pptx," +
                [...SUPPORTED_IMAGE_EXTENSIONS, ...UNSUPPORTED_IMAGE_EXTENSIONS]
                  .map((e) => "." + e).join(",")}
              style={{ display: "none" }}
              onChange={(e) => {
                addFiles(Array.from(e.target.files ?? []));
                e.target.value = ""; // so re-picking the same file still fires a change
              }}
            />
            {/* THE LABEL NAMES ALL THREE WAYS IN, AND KEEPS DOING SO (2026-08-30). It was shortened
                to "Choose files, or drop them here" when this screen moved into a panel, and four
                test suites caught it — correctly. Paste is a real affordance here (`usePasteFiles`),
                and this sentence is the only place a user learns it exists; a button that fits the
                column better but silently drops a feature is a worse button. It wraps to two lines
                in a panel, which is the right trade. */}
            <button type="button" className="btn" onClick={() => inputRef.current?.click()}
                    style={{ height: "auto", padding: "9px 14px", whiteSpace: "normal", lineHeight: 1.4 }}>
              Drop your files and a photo of the property here, paste a screenshot, or choose files
            </button>

            {files.length === 0 ? (
              <p style={{ margin: "9px 0 0", fontSize: 11.5, color: colors.textSecondary }}>
                No files selected yet
              </p>
            ) : (
              <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                {files.map((f, i) => (
                  <li
                    key={`${f.name}:${f.size}:${i}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 5px 3px 9px", borderRadius: 999, background: colors.neutralChip, fontSize: 11.5 }}
                  >
                    <span>
                      {f.name} · {formatBytes(f.size)}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      style={{ border: "none", background: "transparent", color: colors.textSecondary, cursor: "pointer", fontSize: 13, lineHeight: 1, padding: "0 3px" }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* SOURCE PRECEDENCE — placement preview only (owner, 2026-09-23; pending approval). Not
              wired, so it is shown only in a dev build or with `?preview=precedence`; see
              `SourcePrecedence.tsx` for why a customer must not see an unwired control. */}
          {precedencePreviewEnabled(window.location.search, import.meta.env.DEV) && <SourcePrecedencePreview />}

          {/* THE PHOTO SLOT. Present only once a photo has actually been dropped — an empty control
              here would compete with the dropzone above it and imply a second, separate step. */}
          {photos.length > 0 && (
            <section aria-labelledby="vb-photo-heading">
              <h2 id="vb-photo-heading" style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>
                {photos.length === 1 ? "Photo of the home" : "Which photo is the home?"}
              </h2>
              <p style={{ margin: "4px 0 0", fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.5 }}>
                {photos.length === 1
                  ? "This goes at the top of the report. Leave it out and we'll use a Street View image of the address instead."
                  : "Pick the one that goes at the top of the report. The others aren't used — only one subject photo is taken from an upload."}
              </p>
              <ul style={{ listStyle: "none", display: "flex", flexWrap: "wrap", gap: 8, margin: "9px 0 0", padding: 0 }}>
                {photos.map((f, i) => {
                  const chosen = i === subjectIndex;
                  return (
                    <li key={`${f.name}:${f.size}`}>
                      <button
                        type="button"
                        aria-pressed={chosen}
                        aria-label={`Use ${f.name} as the photo of the property`}
                        onClick={() => onIntakeChange((prev) => ({ ...prev, subjectIndex: i }))}
                        style={{
                          padding: 3, borderRadius: 8, cursor: "pointer", background: colors.bgSurface,
                          border: `2px solid ${chosen ? colors.accent : colors.border}`, display: "block",
                        }}
                      >
                        {previews[i] && (
                          <img
                            src={previews[i]}
                            alt={f.name}
                            style={{ display: "block", width: 96, height: 68, objectFit: "cover", borderRadius: 5 }}
                          />
                        )}
                        <span style={{ display: "block", marginTop: 3, fontSize: 10, color: chosen ? colors.accent : colors.textSecondary, maxWidth: 96, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {chosen ? "Subject photo" : f.name}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                /* PHOTO NOTES ARE NOT CLEARED HERE (2026-08-05, review finding). Those notes are the
                   disclosure of what we CHANGED about the agent's files on the way through — a HEIC
                   converted, an oversized image resized — and they are true whether or not the
                   resulting photo is still attached. Removing the photo is a choice about the report;
                   it is not a reason to withdraw a statement about what was done to their file.
                   `photoProblems` is left alone for the same reason. */
                onClick={() => onIntakeChange((prev) => ({ ...prev, photos: [], subjectIndex: 0 }))}
                style={{ marginTop: 8, border: "none", background: "transparent", color: colors.accent, fontSize: 11.5, cursor: "pointer", padding: 0, textDecoration: "underline" }}
              >
                Remove {photos.length === 1 ? "photo" : "photos"} and use Street View instead
              </button>
            </section>
          )}

          {preparing.length > 0 && (
            <p role="status" aria-live="polite" style={{ margin: 0, fontSize: 11.5, color: colors.textSecondary }}>
              Preparing {preparing.join(", ")}…
            </p>
          )}

          {/* A conversion or a resize is disclosed. The agent's file is their evidence; changing it
              quietly is not ours to do. */}
          {photoNotes.length > 0 && (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {photoNotes.map((msg, i) => (
                <li key={i} style={{ fontSize: 11.5, color: colors.textSecondary, marginTop: i ? 4 : 0 }}>{msg}</li>
              ))}
            </ul>
          )}

          {/* An image we still cannot read is said out loud, with the fix, rather than failing at the
              server. */}
          {photoProblems.length > 0 && (
            <ul role="alert" className="note amber" style={{ listStyle: "none", padding: "10px 12px" }}>
              {photoProblems.map((msg, i) => (
                <li key={i} style={{ lineHeight: 1.5, marginTop: i ? 6 : 0 }}>{msg}</li>
              ))}
            </ul>
          )}

          {/* THE SLOWER, CHEAPER READ — AN OPT-OUT, NOT AN OPT-IN (2026-08-07, owner request).
            * The fast read is what happens if this is left alone. Ticking it keeps the file on our own
            * servers: no third party, no per-page cost, and a scanned or unreadable export goes back
            * to taking minutes instead of seconds.
            *
            * THE COPY DOES NOT PROMISE A SPEED THE FALLBACK CANNOT DELIVER. The fast read is an
            * OPERATOR switch (`VALUBRIEF_MATHPIX_ENABLED`) that can be off, out of budget, or simply
            * down, and when it is the local read runs regardless of what this box says — silently, by
            * design. So the unticked case is worded "usually back in seconds" rather than as a
            * guarantee. Promising fifteen seconds and then taking four minutes with nothing on screen
            * to explain it is the one thing this control must not do.
            *
            * NO VENDOR IS NAMED, matching the disclosure the agent gets during the read
            * (`server.mathpix.stage_note`): they did not choose a converter, so they are told what
            * happens to their file, not whose logo is on it. */}
          <label
            style={{ display: "flex", alignItems: "flex-start", gap: 9,
                     cursor: busy ? "not-allowed" : "pointer" }}
          >
            <input
              type="checkbox"
              checked={localReadOnly}
              disabled={busy}
              onChange={(e) => setLocalReadOnly(e.target.checked)}
              aria-describedby="local-read-only-help"
              style={{ marginTop: 2, width: 15, height: 15, cursor: busy ? "not-allowed" : "pointer" }}
            />
            <span>
              <span style={{ fontSize: 12.5, color: colors.textPrimary }}>
                Read this file on our own servers (slower, costs less)
              </span>
              <span
                id="local-read-only-help"
                style={{ display: "block", fontSize: 11.5, color: colors.textSecondary, marginTop: 2,
                         lineHeight: 1.5 }}
              >
                Leave this off and a scanned PDF goes out to a reading service, usually back in
                seconds. That&apos;s the default. Tick it and the file stays with us: it costs less
                and nothing goes to a third party, but a scanned PDF can take a few minutes.
              </span>
            </span>
          </label>
        </div>
        </div>{/* /.vb-panel-pair */}
      </div>

      {/* §6: the SERVER's own refusal, rendered in place when the last attempt actually came back
          429 limit_reached — the race case proactive disabling (below) cannot see coming. */}
      {limitRefusalMessage && <ServerLimitNotice message={limitRefusalMessage} />}

      {/* §6, board 1 option C: the in-flow line above the action that pulls public records and
          any uploaded comps — this screen's "Pull comps" equivalent. Shown whenever usage is
          known, not only at the limit, so the count is visible before the click, not only after
          it is refused. */}
      {usage && <UsageLineView usage={usage} action="pull" />}

      {/* THE FOOT: the consequence on the left, the action on the right — the prototype's own
          arrangement, and the reason the button is no longer eight paragraphs down a column. */}
      <div className="foot">
        <span className="n">
          {/* UI-054: mounted permanently so the announcement fires on content change, not on mount.
              The wait we quote follows the read the agent actually chose (2026-08-07): stating "up to
              a minute" under a ticked "slower" box would be telling them the opposite of what they
              just asked for, and it was never true for a scanned export read locally (~258s on a
              20-page one). */}
          <span role="status" aria-live="polite" style={{ display: "block", minHeight: 17 }}>
            {/* WHAT IS STOPPING YOU, IN THE PLACE YOU ARE LOOKING (2026-09-07). The button's own
                `title` carried this and a tooltip is invisible to anyone who does not hover — which
                on a first visit is everyone. The sentence beside the button is where the eye
                already is, so the one thing still needed is said there.

                §6: THE LIMIT TAKES PRIORITY over the normal explanatory text once the agent has
                actually armed the button — it is the real reason nothing happens on click, and the
                dedicated `LimitNoticeView` below repeats it with the figure and the reset date. */}
            {armed && limited
              ? "Paused for this month's report limit — see below."
              : !armed && !busy && !resolving
              ? "Type the property's address above to start — or drop a file that names it. Nothing else on this screen is required."
              : resolving && !busy
              ? "Looking up this address and any recent nearby sales…"
              : busy
              ? (localReadOnly
                  ? "Reading your files here — a scanned MLS PDF can take several minutes."
                  : "Reading your files — a scanned MLS PDF is usually back in seconds, sometimes a few minutes.")
              : files.length > 0
              ? "We'll read your file first, then fill anything it left blank from public records. The next screen shows you what we found."
              : "We'll look this address up and pull recent nearby sales from public records. The next screen shows you what we found, so you can check it before anything is calculated."}
          </span>
          {armed && limited && usage && <LimitNoticeView usage={usage} />}
        </span>
        <div className="r">
          {/* ONE OBVIOUS ACTION, AND THE FILE IS NO LONGER PART OF ARMING IT (2026-08-11).
              This was `disabled={files.length === 0 || busy}`: no file, no way forward, on a product
              whose whole point is that an agent WITHOUT MLS membership can still produce a CMA. The
              address arms it now.
              IT DOES NOT *REQUIRE* THE ADDRESS, deliberately. Spec §4 says "the address field is the
              only thing on screen 1 that must be filled", which is a statement about the FILE being
              optional — and the non-negotiable one screen up in the same document is that nothing
              existing is taken away. Uploading a sheet that names its own subject, with no typed
              address, has always worked and is the path GATE-004's mismatch machinery is built
              around. So: an address, or a file.
              ONE LABEL IN BOTH CASES — a button whose words change depending on what you have
              attached is a second thing to read before the first thing you do.
              §6: `limited` joins the gate — every control the owner's refusal list names must be
              disabled at the limit, this screen's included, with the reason stated above rather
              than left to a bare disabled state. */}
          <button
            type="button"
            className="btn p"
            disabled={!armed || limited}
            title={
              limited
                ? "Paused for this month's report limit"
                : addressLine.trim() || files.length ? undefined : "Enter the subject's address to start"
            }
            /* `subjectGrid` no longer rides along: the flow already OWNS it (this screen edits the
               flow's copy directly), so handing it back on submit would be handing it its own state. */
            onClick={() => onStart({ files, addressLine, subjectPhoto, localReadOnly })}
          >
            {/* THE LABEL NOW NAMES THE SCREEN IT OPENS (2026-09-07). It said "Find the comparable
                sales" and it does not go to the comparable sales — it goes to step 2, the property.
                Measured by walking the built app: press it and the rail moves 1 -> 2 while the
                words promise step 3. A forward control that names the wrong destination is worse
                than one with no words on it, because the agent stops trusting the rail as well. */}
            {busy ? "Reading files…" : resolving ? "Looking…" : "Next — check what we found"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * UI-052 — the report tier, chosen where it is actually consumed.
 *
 * `tier` is sent into `startExtract` from THIS screen, but its only control used to live in
 * `ReadinessGate`, inside the `if (ready)` branch — so a user whose file was missing a required
 * field never saw the choice at all, and everyone else chose after the thing they were choosing
 * had already run. The hints are rendered as visible text: a `title` tooltip is invisible on a
 * trackpad in a hurry.
 */
function TierPicker({ tier, onChange }: { tier: ReportTier; onChange: (t: ReportTier) => void }) {
  /* THE PROTOTYPE'S OWN CONTROL (2026-08-30). `.typeGrid` + `.typeOpt` are the classes the approved
     mock uses for exactly this shape of choice — a row of cards that collapses to one column below
     150px each, an accent left-edge on the selected one, a 13.5px title over an 11px description.
     They have been in `mock-design.css` since the port; this control simply now uses them instead of
     re-declaring the same thing in inline styles that were close but not equal.

     STILL A `fieldset`/`legend` WITH `aria-pressed` BUTTONS. The prototype is markup for looking at;
     the accessible grouping is ours to keep, and the visual result is identical either way. */
  return (
    <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
      <legend className="sr-only-legend" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
        Report type
      </legend>
      <div className="typeGrid">
        {REPORT_TIERS.map((t) => {
          const selected = t === tier;
          return (
            <button
              key={t}
              type="button"
              className={selected ? "typeOpt on" : "typeOpt"}
              aria-pressed={selected}
              onClick={() => onChange(t)}
            >
              <span className="t">{REPORT_TIER_LABELS[t]}</span>
              <span className="d">{REPORT_TIER_HINTS[t]}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
